import { useTraduccion } from '@tallerya/i18n';
import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { IconoCheck } from '../iconos';
import './avisos.css';

// El aviso «Guardado» (F-8): la tarjeta con el check verde que aparece arriba a
// la derecha después de guardar. Sale del marco «Productos/Servicios - Listado
// (Guardado)» del Figma.
//
// ## Por qué va con un proveedor y no como un componente suelto
//
// Casi siempre se guarda en una pantalla y el aviso se ve en **otra**: el
// formulario guarda, navega al listado, y es el listado el que muestra «Se
// guardó…». Un componente dentro del formulario moriría con él al navegar. El
// proveedor vive en la raíz de la app, por fuera de las rutas, y sobrevive.
//
//   const { avisar } = useAvisos();
//   avisar({ titulo: `Se guardó «${nombre}»`, texto: 'Ya se puede agregar a las órdenes.' });
//   navigate('/catalogo');

export interface Aviso {
  titulo: string;
  texto?: string;
}

interface Contexto {
  avisar: (aviso: Aviso) => void;
}

const ContextoAvisos = createContext<Contexto | null>(null);

/** Cuánto se queda el aviso a la vista si nadie lo cierra. */
const DURACION_MS = 6000;

/** Va en el `main.tsx`, por fuera del router, para que el aviso sobreviva a la navegación. */
export function ProveedorAvisos({ children }: { children: ReactNode }) {
  // Un aviso a la vez: uno nuevo reemplaza al anterior. `clave` fuerza a volver
  // a contar el tiempo aunque el texto sea el mismo de recién.
  const [actual, setActual] = useState<(Aviso & { clave: number }) | null>(null);

  const avisar = useCallback((aviso: Aviso) => setActual({ ...aviso, clave: Date.now() }), []);
  // Estable: el aviso lo usa en su efecto, y una función nueva en cada dibujado
  // reiniciaría la cuenta de los seis segundos.
  const cerrar = useCallback(() => setActual(null), []);
  const valor = useMemo(() => ({ avisar }), [avisar]);

  return (
    <ContextoAvisos value={valor}>
      {children}
      {/* La región existe siempre, vacía o no: el lector de pantalla solo anuncia
          lo que cambia DENTRO de una región que ya estaba en la página. */}
      <div className="ui-avisos" role="status" aria-live="polite">
        {actual && <AvisoGuardado key={actual.clave} aviso={actual} onCerrar={cerrar} />}
      </div>
    </ContextoAvisos>
  );
}

/** Para mostrar un aviso desde cualquier pantalla. Falla en voz alta fuera del proveedor. */
export function useAvisos(): Contexto {
  const contexto = use(ContextoAvisos);
  if (!contexto) {
    throw new Error('useAvisos() necesita estar dentro de <ProveedorAvisos>.');
  }
  return contexto;
}

function AvisoGuardado({ aviso, onCerrar }: { aviso: Aviso; onCerrar: () => void }) {
  const { t } = useTraduccion();

  useEffect(() => {
    const temporizador = setTimeout(onCerrar, DURACION_MS);
    return () => clearTimeout(temporizador);
  }, [onCerrar]);

  return (
    <div className="ui-aviso-guardado">
      <span className="ui-aviso-guardado__marca">
        <IconoCheck />
      </span>
      <div className="ui-aviso-guardado__texto">
        <strong>{aviso.titulo}</strong>
        {aviso.texto && <span>{aviso.texto}</span>}
      </div>
      <button type="button" className="ui-aviso-guardado__cerrar" aria-label={t('ui.avisos.cerrar')} onClick={onCerrar}>
        ✕
      </button>
    </div>
  );
}
